import _ from '@lodash';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import FuseLoading from '@fuse/core/FuseLoading';
import { ThemeProvider } from '@material-ui/core/styles';
import { useState } from 'react';
import { selectMainTheme } from 'app/store/fuse/settingsSlice';
import { showMessage } from 'app/store/fuse/messageSlice';
import { useFormContext } from 'react-hook-form';
import { Button, Icon, Dialog, DialogTitle, DialogContent, DialogActions } from '@material-ui/core';
import HysysModelEditService from '../service/HysysModelEditService';
import hysysAttributeInfoTestResponse from './getHysysAttributeInfo.json';
import hysysInputAttributeValuesTestResponse from './getHysysInputAttributeValues.json';

const INPUT_ATTRIBUTES_FIELD = 'inputAttributes';
const OUTPUT_ATTRIBUTES_FIELD = 'outputAttributes';

const isAttributeMap = value => _.isObject(value) && !_.isArray(value);
const isComponentAttributeValue = value => isAttributeMap(value) || _.isArray(value);

const getAttributeValue = value => (_.isArray(value) ? null : value);

const getComponents = value => {
	if (_.isArray(value)) return value;
	if (isAttributeMap(value)) return Object.keys(value);
	return [];
};

const getSection = (data, snakeKey, camelKey) => {
	if (!isAttributeMap(data)) return null;

	const section = data[snakeKey] || data[camelKey];
	return isAttributeMap(section) ? section : null;
};

const getInputAttributeMap = data => getSection(data, 'input_attributes', 'inputAttributes');
const getOutputAttributeMap = data => getSection(data, 'output_attributes', 'outputAttributes');

const getInputValueMap = data => getInputAttributeMap(data) || (isAttributeMap(data) ? data : null);
const getOutputValueMap = data => getOutputAttributeMap(data);

const fillWithNull = obj => {
	const result = { ...obj };
	Object.keys(result).forEach(key => {
		result[key] = null;
	});
	return result;
};

const getAttributeType = (value, currentType) => {
	if (currentType) return currentType;
	return isComponentAttributeValue(value) ? 'ComponentMassFrac' : 'Variable';
};

const createAttribute = ({ name, value, field, orderIndex }) => {
	const type = getAttributeType(value);
	const isComponent = type === 'ComponentMassFrac' || type === 'ComponentMassFlow';
	const common = {
		id: null,
		name,
		type,
		spreadsheet: null,
		cell: null,
		value: getAttributeValue(value),
		uom: null,
		expression: type === 'Variable' ? name : null,
		stream: isComponent ? name : null,
		useUom: false,
		orderIndex
	};

	if (field === INPUT_ATTRIBUTES_FIELD) {
		return {
			...common,
			minValue: isAttributeMap(value) ? fillWithNull(value) : null,
			maxValue: isAttributeMap(value) ? fillWithNull(value) : null
		};
	}

	return {
		...common,
		components: getComponents(value)
	};
};

const mergeAttributeMap = ({ currentAttributes, attributeMap, field, setBounds }) => {
	const result = [...currentAttributes];
	const counts = { added: 0, updated: 0, nullValues: 0 };

	Object.keys(attributeMap).forEach((name, index) => {
		const value = attributeMap[name];
		const currentIndex = result.findIndex(attribute => attribute && attribute.name === name);

		if (value == null) counts.nullValues += 1;

		if (currentIndex > -1) {
			const current = result[currentIndex];
			const type = getAttributeType(value, current.type);
			const isComponent = type === 'ComponentMassFrac' || type === 'ComponentMassFlow';

			result[currentIndex] = {
				...current,
				name,
				type,
				value: getAttributeValue(value),
				...(isComponent && !current.stream ? { stream: name } : {}),
				...(type === 'Variable' && !current.expression ? { expression: name } : {}),
				...(field === OUTPUT_ATTRIBUTES_FIELD && isComponentAttributeValue(value) ? { components: getComponents(value) } : {}),
				...(field === INPUT_ATTRIBUTES_FIELD && setBounds
					? {
						minValue: isAttributeMap(value) ? fillWithNull(value) : null,
						maxValue: isAttributeMap(value) ? fillWithNull(value) : null
					}
					: {})
			};
			counts.updated += 1;
		} else {
			result.push(createAttribute({
				name,
				value,
				field,
				orderIndex: currentAttributes.length + index
			}));
			counts.added += 1;
		}
	});

	return { attributes: result, counts };
};

const formatCounts = (label, counts) => `${label}: ${counts.added} added, ${counts.updated} updated${counts.nullValues > 0 ? `, ${counts.nullValues} null values` : ''}`;

function FeedbackMessages(props) {
	const { messages } = props;

	return (
		<div className="flex flex-col gap-8">
			{messages.map((mess, index) => (
				<div
					key={`${mess}-${index}`}
					className="flex items-start p-12"
					style={{
						border: '1px solid rgba(0, 0, 0, 0.12)',
						borderRadius: 8,
						backgroundColor: 'rgba(0, 0, 0, 0.03)'
					}}
				>
					<Icon className="mr-10 text-20 opacity-70">check_circle</Icon>
					<div className="leading-relaxed">{mess}</div>
				</div>
			))}
		</div>
	);
}

function LoadHysysAttributeButtons(props) {
	const { id, useTestResponses } = props;
	const { t } = useTranslation(['common']);
	const mainTheme = useSelector(selectMainTheme);
	const dispatch = useDispatch();
	const { getValues, setValue } = useFormContext();
	const [loading, setLoading] = useState(false);
	const [showDialog, setShowDialog] = useState(false);
	const [dialogTitle, setDialogTitle] = useState('Load HYSYS attributes');
	const [message, setMessage] = useState([]);

	const getAttributeInfo = () => (useTestResponses
		? Promise.resolve(hysysAttributeInfoTestResponse)
		: HysysModelEditService.getHysysAttributeInfo(id));

	const getAttributeValues = () => (useTestResponses
		? Promise.resolve(hysysInputAttributeValuesTestResponse)
		: HysysModelEditService.getHysysInputAttributeValues(id));

	const ensureModel = () => {
		if (id == null || id === 'new') {
			dispatch(showMessage({ message: 'Model is required.' }));
			return false;
		}
		return true;
	};

	const openLoader = title => {
		setDialogTitle(title);
		setMessage([]);
		setLoading(true);
		setShowDialog(true);
	};

	const updateField = (field, attributeMap, options = {}) => {
		if (!isAttributeMap(attributeMap)) {
			return null;
		}

		const { attributes, counts } = mergeAttributeMap({
			currentAttributes: getValues(field) || [],
			attributeMap,
			field,
			setBounds: !!options.setBounds
		});
		setValue(field, attributes, { shouldDirty: true, shouldValidate: true });
		return counts;
	};

	const loadInput = () => {
		if (!ensureModel()) return;

		openLoader('Load Input');
		getAttributeInfo()
			.then(attributeInfo => {
				const inputAttributes = getInputAttributeMap(attributeInfo);
				const attributeCounts = updateField(INPUT_ATTRIBUTES_FIELD, inputAttributes, { setBounds: true });

				return getAttributeValues()
					.then(attributeValues => {
						const inputValues = getInputValueMap(attributeValues);
						const valueCounts = updateField(INPUT_ATTRIBUTES_FIELD, inputValues);
						return { attributeCounts, valueCounts };
					});
			})
			.then(({ attributeCounts, valueCounts }) => {
				const messages = [];
				if (attributeCounts) messages.push(formatCounts('Input attributes', attributeCounts));
				if (valueCounts) messages.push(formatCounts('Input values', valueCounts));
				setMessage(messages.length > 0 ? messages : ['No input attributes found.']);
				dispatch(showMessage({ message: 'HYSYS input loaded.' }));
			})
			.catch(handleCatch)
			.finally(() => setLoading(false));
	};

	const loadOutput = () => {
		if (!ensureModel()) return;

		openLoader('Load Output');
		getAttributeInfo()
			.then(attributeInfo => {
				const outputAttributes = getOutputAttributeMap(attributeInfo);
				const attributeCounts = updateField(OUTPUT_ATTRIBUTES_FIELD, outputAttributes);

				return getAttributeValues()
					.then(attributeValues => {
						const outputValues = getOutputValueMap(attributeValues) || outputAttributes;
						const valueCounts = updateField(OUTPUT_ATTRIBUTES_FIELD, outputValues);
						return { attributeCounts, valueCounts };
					});
			})
			.then(({ attributeCounts, valueCounts }) => {
				const messages = [];
				if (attributeCounts) messages.push(formatCounts('Output attributes', attributeCounts));
				if (valueCounts) messages.push(formatCounts('Output values', valueCounts));
				setMessage(messages.length > 0 ? messages : ['No output attributes found.']);
				dispatch(showMessage({ message: 'HYSYS output loaded.' }));
			})
			.catch(handleCatch)
			.finally(() => setLoading(false));
	};

	const loadAll = () => {
		if (!ensureModel()) return;

		openLoader('Load All');
		Promise.all([getAttributeInfo(), getAttributeValues()])
			.then(([attributeInfo, attributeValues]) => {
				const inputAttributeCounts = updateField(INPUT_ATTRIBUTES_FIELD, getInputAttributeMap(attributeInfo), { setBounds: true });
				const inputValueCounts = updateField(INPUT_ATTRIBUTES_FIELD, getInputValueMap(attributeValues));
				const outputAttributeMap = getOutputAttributeMap(attributeInfo);
				const outputAttributeCounts = updateField(OUTPUT_ATTRIBUTES_FIELD, outputAttributeMap);
				const outputValueCounts = updateField(OUTPUT_ATTRIBUTES_FIELD, getOutputValueMap(attributeValues) || outputAttributeMap);

				const messages = [];
				if (inputAttributeCounts) messages.push(formatCounts('Input attributes', inputAttributeCounts));
				if (inputValueCounts) messages.push(formatCounts('Input values', inputValueCounts));
				if (outputAttributeCounts) messages.push(formatCounts('Output attributes', outputAttributeCounts));
				if (outputValueCounts) messages.push(formatCounts('Output values', outputValueCounts));
				setMessage(messages.length > 0 ? messages : ['No HYSYS attributes found.']);
				dispatch(showMessage({ message: 'HYSYS attributes loaded.' }));
			})
			.catch(handleCatch)
			.finally(() => setLoading(false));
	};

	const handleCatch = err => {
		console.log(err);
		setMessage([`Connection problem: ${err !== null ? err : 'Unknown'}`]);
	};

	return (
		<>
			<Button className="whitespace-nowrap mx-4" variant="contained" color="secondary" disabled={id === 'new' || loading} onClick={() => loadInput()} startIcon={<Icon className="hidden sm:flex">graphic_eq</Icon>}>
				Load Input Attributes
			</Button>
			<Button className="whitespace-nowrap mx-4" variant="contained" color="secondary" disabled={id === 'new' || loading} onClick={() => loadOutput()} startIcon={<Icon className="hidden sm:flex">graphic_eq</Icon>}>
				Load Output Attributes
			</Button>
			<Button className="whitespace-nowrap mx-4" variant="contained" color="secondary" disabled={id === 'new' || loading} onClick={() => loadAll()} startIcon={<Icon className="hidden sm:flex">graphic_eq</Icon>}>
				Load All Attributes
			</Button>
			<ThemeProvider theme={mainTheme}>
				<Dialog open={showDialog} fullWidth>
					<DialogTitle disableTypography className="border-b">
						{dialogTitle}
					</DialogTitle>
					<DialogContent className="p-20">
						{loading && <FuseLoading />}
						{!loading && message.length > 0 && <FeedbackMessages messages={message} />}
						{!loading && message.length === 0 && <div className="flex justify-center">No result</div>}
					</DialogContent>
					<DialogActions className="justify-between border-t p-20">
						<Button
							onClick={() => {
								setShowDialog(false);
							}}
						>
							{t('close')}
						</Button>
					</DialogActions>
				</Dialog>
			</ThemeProvider>
		</>
	);
}

export default LoadHysysAttributeButtons;
